import { useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format } from 'date-fns';
import {
  AlertTriangle,
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Filter,
  HandshakeIcon,
  Inbox,
  Info,
  Loader2,
  Mail,
  Maximize2,
  MoreVertical,
  Phone,
  Pin,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  UserCog,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogCallModal, ReassignUserModal, UpdateStatusModal } from '@/components/collections/action-modals';
import { CaseSendEmailModal } from '@/components/collections/case-send-email-modal';
import { CreatePromiseDrawer } from '@/components/collections/create-promise-drawer';
import { KPICard } from '@/components/collections/kpi-card';
import { LastActionCell } from '@/components/collections/last-action-cell';
import { StageSkipConfirmationModal } from '@/components/collections/stage-skip-confirmation-modal';
import { UpdateNextActionDialog } from '@/components/collections/update-next-action-dialog';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpSupportButton } from '@/components/help-support';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { type CurrencyMode, CurrencyToggle } from '@/components/shared/currency-toggle';
import { useCanWrite } from '@/components/auth/useCanWrite';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TableCell, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useActivityTypeFilterOptions } from '@/hooks/useActivityTypesData';
import { useCaseTransactions } from '@/hooks/useCaseDetailData';
import { useCaseTransactionIds } from '@/hooks/useCaseTransactionLinks';
import { useBulkReassignCases, useBulkUpdateCaseStatus } from '@/hooks/useCollectionCasesData';
import {
  buildGraphQLFilterVariable,
  type GetCollectionCasesParams,
  useCollectionCasesPageData,
  useCollectionCaseTotals,
} from '@/hooks/useCollectionCasesGraphQL';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { useExportAll } from '@/hooks/useExportAll';
import { type StageSkipConfirmation, useRecordStageSkipDecision } from '@/hooks/useLogCaseEmail';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { useStrategiesListGraphQL } from '@/hooks/useStrategiesListGraphQL';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { getPageNumbers } from '@/lib/pagination-utils';
import { isRangeInvalid, parseRangeInput, RANGE_MIN_GREATER_THAN_MAX_MESSAGE } from '@/lib/range-filter-validation';
import { getPersistedFilters, setPersistedFilters } from '@/lib/session-storage-filters';
import { getPinnedColumnStyle, useColumnWidthObserver } from '@/lib/table-pinning';
import { cn, formatCurrency, formatDate, formatDateDDMMYYYY, formatDateTime, getCurrencySymbol } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { CollectionCase } from '@/types/collections';
import type { ColumnInput } from '@/types/export';

export const COLLECTIONS_QUEUE_FILTERS_KEY = 'collections-queue-filters';

interface PersistedCollectionsQueueFilters {
  advancedFilters: {
    statuses: string[];
    priority: string;
    assignedUser: string;
    currencies: string[];
    nextActionDateFrom: string;
    nextActionDateTo: string;
    outstandingMin: number | undefined;
    outstandingMax: number | undefined;
    daysInCollectionMin: number | undefined;
    daysInCollectionMax: number | undefined;
    strategies: string[];
    stageMin: number | undefined;
    stageMax: number | undefined;
    grossMin: number | undefined;
    grossMax: number | undefined;
    createdFrom: string;
    createdTo: string;
    updatedFrom: string;
    updatedTo: string;
    lastActionTypes: string[];
  };
  searchQuery: string;
}

const DEFAULT_QUEUE_COLUMNS: ColumnDefinition[] = [
  { id: 'select', label: 'Select', visible: true, order: 0, locked: true },
  { id: 'caseId', label: 'Case Number', visible: true, order: 1 },
  { id: 'priority', label: 'Priority', visible: true, order: 2 },
  { id: 'customerName', label: 'Customer', visible: true, order: 3 },
  { id: 'currency', label: 'Currency', visible: true, order: 4 },
  { id: 'gross', label: 'Gross', visible: true, order: 5 },
  { id: 'outstanding', label: 'Outstanding', visible: true, order: 6 },
  { id: 'daysInCollection', label: 'Days', visible: true, order: 7 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 8 },
  { id: 'nextAction', label: 'Next Action', visible: true, order: 9 },
  { id: 'nextActionDate', label: 'Action Due Date', visible: true, order: 10 },
  { id: 'status', label: 'Status', visible: true, order: 11 },
  { id: 'strategy', label: 'Collection Strategy', visible: true, order: 12 },
  { id: 'stage', label: 'Stage', visible: true, order: 13 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 14 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 15 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 16 },
  { id: 'assignedTo', label: 'Assigned To', visible: false, order: 17 },
  { id: 'createdAt', label: 'Created Date', visible: false, order: 18 },
  { id: 'updatedAt', label: 'Last Updated', visible: false, order: 19 },
  { id: 'transactionCount', label: 'No. of Transactions', visible: false, order: 20 },
];

// Helper function to get the latest action for a case
const getLatestAction = (caseItem: CollectionCase) => {
  if (caseItem.lastAction) {
    return {
      type: caseItem.lastAction.type,
      description: caseItem.lastAction.description,
      performedBy: caseItem.lastAction.performedBy,
      date: caseItem.lastAction.date,
    };
  }
  return null;
};

export default function CollectionsQueue() {
  return (
    <ExportProvider>
      <CollectionsQueueInner />
    </ExportProvider>
  );
}

function CollectionsQueueInner() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite } = useCanWrite();
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const baseCurrency = selectedCompany?.currency || 'GBP';
  const currencySymbol = getCurrencySymbol(baseCurrency);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('customer');
  const filterParam = searchParams.get('filter');
  const assignedUserParam = searchParams.get('assignedUser');
  const statusParam = searchParams.get('status');
  const currenciesParam = searchParams.get('currencies');

  const persistedFilters = useMemo(
    () => getPersistedFilters<PersistedCollectionsQueueFilters>(COLLECTIONS_QUEUE_FILTERS_KEY),
    [],
  );
  const [searchQuery, setSearchQuery] = useState(persistedFilters?.searchQuery ?? '');
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  const [stageConfirmation, setStageConfirmation] = useState<StageSkipConfirmation | null>(null);
  const recordStageSkipDecision = useRecordStageSkipDecision();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Modal states
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [showUpdateNextAction, setShowUpdateNextAction] = useState(false);
  const [reassignUserOpen, setReassignUserOpen] = useState(false);
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);

  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Filter drawer state (matches Promises page pattern — multi-select statuses)
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const defaultAdvancedFilters = useMemo(
    () => ({
      statuses: ['Active', 'Promised', 'Escalated', 'On Hold'] as string[],
      priority: 'all',
      assignedUser: 'all',
      currencies: [] as string[],
      nextActionDateFrom: '',
      nextActionDateTo: '',
      outstandingMin: undefined as number | undefined,
      outstandingMax: undefined as number | undefined,
      daysInCollectionMin: undefined as number | undefined,
      daysInCollectionMax: undefined as number | undefined,
      strategies: [] as string[],
      stageMin: undefined as number | undefined,
      stageMax: undefined as number | undefined,
      grossMin: undefined as number | undefined,
      grossMax: undefined as number | undefined,
      createdFrom: '',
      createdTo: '',
      updatedFrom: '',
      updatedTo: '',
      lastActionTypes: [] as string[],
    }),
    [],
  );
  type AdvancedFilters = typeof defaultAdvancedFilters;
  // When arriving via a dashboard drill-through the URL carries filter params (applied by the
  // effects below); those should win over any previously persisted filters, so start from
  // defaults in that case rather than restoring a stale persisted user/status.
  const hasUrlFilters = useMemo(() => searchParams.toString().length > 0, []); // eslint-disable-line react-hooks/exhaustive-deps -- read URL params only on mount
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(
    !hasUrlFilters && persistedFilters?.advancedFilters ? persistedFilters.advancedFilters : defaultAdvancedFilters,
  );
  const [draftFilters, setDraftFilters] = useState<AdvancedFilters>(defaultAdvancedFilters);

  const handleOpenMoreFilters = useCallback(() => {
    setDraftFilters({ ...advancedFilters });
    setMoreFiltersOpen(true);
  }, [advancedFilters]);

  const draftRangeErrors = {
    outstanding: isRangeInvalid(draftFilters.outstandingMin, draftFilters.outstandingMax),
    daysInCollection: isRangeInvalid(draftFilters.daysInCollectionMin, draftFilters.daysInCollectionMax),
    stage: isRangeInvalid(draftFilters.stageMin, draftFilters.stageMax),
    gross: isRangeInvalid(draftFilters.grossMin, draftFilters.grossMax),
  };
  const hasInvalidRange = Object.values(draftRangeErrors).some(Boolean);

  const handleApplyFilters = useCallback(() => {
    if (hasInvalidRange) return;

    setAdvancedFilters({ ...draftFilters });
    setMoreFiltersOpen(false);
  }, [draftFilters, hasInvalidRange]);

  const handleClearDraftFilters = useCallback(() => {
    setDraftFilters({ ...defaultAdvancedFilters });
  }, [defaultAdvancedFilters]);

  const handleCloseMoreFilters = useCallback((open: boolean) => {
    if (!open) {
      setMoreFiltersOpen(false);
    }
  }, []);

  // Persist filter state to sessionStorage on change
  useEffect(() => {
    const state: PersistedCollectionsQueueFilters = { advancedFilters, searchQuery };
    setPersistedFilters(COLLECTIONS_QUEUE_FILTERS_KEY, state);
  }, [advancedFilters, searchQuery]);

  const isFiltered =
    searchQuery.trim() !== '' || JSON.stringify(advancedFilters) !== JSON.stringify(defaultAdvancedFilters);

  // Count active filters for badge display
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (JSON.stringify(advancedFilters.statuses) !== JSON.stringify(defaultAdvancedFilters.statuses)) count += 1;
    if (advancedFilters.priority !== 'all') count += 1;
    if (advancedFilters.assignedUser !== 'all') count += 1;
    if (advancedFilters.currencies.length > 0) count += 1;
    if (advancedFilters.nextActionDateFrom || advancedFilters.nextActionDateTo) count += 1;
    if (advancedFilters.outstandingMin !== undefined || advancedFilters.outstandingMax !== undefined) count += 1;
    if (advancedFilters.daysInCollectionMin !== undefined || advancedFilters.daysInCollectionMax !== undefined)
      count += 1;
    if (advancedFilters.strategies.length > 0) count += 1;
    if (advancedFilters.stageMin !== undefined || advancedFilters.stageMax !== undefined) count += 1;
    if (advancedFilters.grossMin !== undefined || advancedFilters.grossMax !== undefined) count += 1;
    if (advancedFilters.createdFrom || advancedFilters.createdTo) count += 1;
    if (advancedFilters.updatedFrom || advancedFilters.updatedTo) count += 1;
    if (advancedFilters.lastActionTypes.length > 0) count += 1;
    return count;
  }, [advancedFilters, defaultAdvancedFilters]);

  // Column preferences
  const {
    columns,
    visibleColumns: baseVisibleColumnDefs,
    savePreferences,
    resetToDefaults,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CollectionsQueue, DEFAULT_QUEUE_COLUMNS);
  const baseVisibleColumns = baseVisibleColumnDefs.map((col) => col.id);
  const visibleColumns =
    columnOrder.length > 0
      ? columnOrder
          .filter((id) => baseVisibleColumns.includes(id))
          .concat(baseVisibleColumns.filter((id) => !columnOrder.includes(id)))
      : baseVisibleColumns;
  const setVisibleColumns = (newCols: string[]) => setColumnOrder(newCols);

  // Column resize state
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const { widths: measuredColumnWidths, measureRef } = useColumnWidthObserver();

  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const handleResizeMouseDown = (e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const current = resizingRef.current;
      const delta = ev.clientX - current.startX;
      const newWidth = Math.max(60, current.startWidth + delta);
      setLocalColumnWidths((prev) => ({ ...prev, [current.columnId]: newWidth }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Column reorder state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    const newPinnedRight = pinnedRight.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    const newPinnedLeft = pinnedLeft.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handleResetColumnWidths = () => resetColumnWidths();
  const handleResetColumnOrder = () => resetColumnOrder();

  // Sorting state
  type SortField =
    | 'priority'
    | 'caseId'
    | 'customerName'
    | 'currency'
    | 'gross'
    | 'outstanding'
    | 'daysInCollection'
    | 'transactionCount'
    | 'nextAction'
    | 'nextActionDate'
    | 'status'
    | 'strategy'
    | 'stage'
    | 'customerAccountNumber'
    | 'customerEmail'
    | 'customerPhone'
    | 'assignedTo'
    | 'createdAt'
    | 'updatedAt';
  type SortDirection = 'asc' | 'desc' | null;
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<
    SortField | null,
    SortDirection
  >(UniqueTableNamesForColumnConfiguration.CollectionsQueue, null, null);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, advancedFilters, sortField, sortDirection]);

  // Build query params for server-side filtering, sorting, and pagination
  const queryParams: GetCollectionCasesParams = useMemo(() => {
    const params: GetCollectionCasesParams = {
      page: currentPage,
      pageSize: itemsPerPage,
      sortBy: sortField ?? undefined,
      sortOrder: sortDirection ?? undefined,
    };
    if (searchQuery) params.search = searchQuery;
    if (advancedFilters.statuses.length > 0) params.statuses = advancedFilters.statuses;
    if (advancedFilters.priority && advancedFilters.priority !== 'all') params.priority = advancedFilters.priority;
    if (advancedFilters.assignedUser && advancedFilters.assignedUser !== 'all') {
      const userId = Number(advancedFilters.assignedUser);
      if (!isNaN(userId)) params.assignedToUserId = userId;
    }
    if (advancedFilters.currencies.length > 0) {
      params.currencies = advancedFilters.currencies;
    }
    if (advancedFilters.nextActionDateFrom) params.nextActionDateFrom = advancedFilters.nextActionDateFrom;
    if (advancedFilters.nextActionDateTo) params.nextActionDateTo = advancedFilters.nextActionDateTo;
    if (advancedFilters.outstandingMin !== undefined) params.outstandingMin = advancedFilters.outstandingMin;
    if (advancedFilters.outstandingMax !== undefined) params.outstandingMax = advancedFilters.outstandingMax;
    if (advancedFilters.daysInCollectionMin !== undefined)
      params.daysInCollectionMin = advancedFilters.daysInCollectionMin;
    if (advancedFilters.daysInCollectionMax !== undefined)
      params.daysInCollectionMax = advancedFilters.daysInCollectionMax;
    if (advancedFilters.strategies.length > 0) params.strategies = advancedFilters.strategies;
    if (advancedFilters.stageMin !== undefined) params.stageMin = advancedFilters.stageMin;
    if (advancedFilters.stageMax !== undefined) params.stageMax = advancedFilters.stageMax;
    if (advancedFilters.grossMin !== undefined) params.grossMin = advancedFilters.grossMin;
    if (advancedFilters.grossMax !== undefined) params.grossMax = advancedFilters.grossMax;
    if (advancedFilters.createdFrom) params.createdFrom = advancedFilters.createdFrom;
    if (advancedFilters.createdTo) params.createdTo = advancedFilters.createdTo;
    if (advancedFilters.updatedFrom) params.updatedFrom = advancedFilters.updatedFrom;
    if (advancedFilters.updatedTo) params.updatedTo = advancedFilters.updatedTo;
    if (advancedFilters.lastActionTypes.length > 0) params.lastActionTypes = advancedFilters.lastActionTypes;
    return params;
  }, [currentPage, itemsPerPage, searchQuery, advancedFilters, sortField, sortDirection]);

  // GraphQL-backed hook: server-side filtering, sorting, and pagination
  const { cases, pagination, isLoading } = useCollectionCasesPageData(queryParams);

  // Fetch currencies list from the REST API for the currency filter dropdown
  const { data: currenciesList = [] } = useCurrenciesList();

  // Fetch strategies list for the Collection Strategy multi-select filter
  const { data: strategiesList = [] } = useStrategiesListGraphQL({ pageSize: 200 });
  const strategyFilterOptions = useMemo(
    () =>
      [...new Set(strategiesList.filter((s) => s.name && s.status === 'Active').map((s) => s.name))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [strategiesList],
  );
  const currencyFilterOptions = useMemo(() => currenciesList.map((c) => c.code), [currenciesList]);

  // Fetch the canonical activity-type lookup for the Last Action Type filter dropdown
  const { data: actionTypeOptions = [] } = useActivityTypeFilterOptions();

  // Server-side totals for KPI cards (applies to all pages, not just the current page)
  const { data: totals, isLoading: isTotalsLoading } = useCollectionCaseTotals(queryParams);

  // Fetch system users for the Users filter dropdown (respects demo/live mode)
  const { data: activeUsers = [] } = useCompanyUsersList();

  // Bulk mutation hooks for collection cases
  const bulkReassignMutation = useBulkReassignCases();
  const bulkUpdateStatusMutation = useBulkUpdateCaseStatus();

  // Map active users to the format expected by ReassignUserModal
  const reassignableUsers = useMemo(
    () =>
      activeUsers.map((user) => ({
        id: String(user.id),
        name: `${user.firstName} ${user.lastName}`.trim() || `User ${user.id}`,
        avatar: user.avatar ?? '',
      })),
    [activeUsers],
  );

  // Get transactions for the selected case to include as linked invoices in promise creation
  const { data: caseTransactionsData = [] } = useCaseTransactions(
    selectedCase?.customerId || undefined,
    selectedCase?.customerName || '',
    selectedCase?.displayOutstandingAmount ?? selectedCase?.baseAmount ?? 0,
  );
  const { data: caseLinkedTransactionIds = [] } = useCaseTransactionIds(selectedCase?.id || undefined);

  // Handle URL filter parameter
  useEffect(() => {
    if (filterParam === 'action-due-today') {
      // When coming from Homepage "Cases Due" metric, reset to default filters
      setAdvancedFilters(defaultAdvancedFilters);
    }
  }, [filterParam, defaultAdvancedFilters]);

  // Handle status URL param — e.g. ?status=Active or ?status=Active,Promised
  useEffect(() => {
    if (statusParam) {
      const statuses = statusParam.split(',').filter(Boolean);
      setAdvancedFilters((prev) => ({ ...prev, statuses }));
    }
  }, [statusParam]);

  // Handle assignedUser URL param — accept a numeric user ID directly, or resolve a display name once activeUsers load
  useEffect(() => {
    if (!assignedUserParam) return;
    if (/^\d+$/.test(assignedUserParam)) {
      setAdvancedFilters((prev) =>
        prev.assignedUser === assignedUserParam ? prev : { ...prev, assignedUser: assignedUserParam },
      );
      return;
    }
    if (activeUsers.length > 0) {
      const match = activeUsers.find(
        (u) => `${u.firstName} ${u.lastName}`.trim().toLowerCase() === assignedUserParam.toLowerCase(),
      );
      if (match) {
        const matchedId = String(match.id);
        setAdvancedFilters((prev) => (prev.assignedUser === matchedId ? prev : { ...prev, assignedUser: matchedId }));
      }
    }
  }, [assignedUserParam, activeUsers]);

  // Handle currencies URL param — e.g. ?currencies=GBP
  useEffect(() => {
    if (currenciesParam) {
      const codes = currenciesParam.split(',').filter(Boolean);
      if (codes.length > 0) {
        setAdvancedFilters((prev) => ({ ...prev, currencies: codes }));
      }
    }
  }, [currenciesParam]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, advancedFilters, filterParam]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> no sort (null)
      if (sortDirection === 'asc') {
        setSort(field, 'desc');
      } else if (sortDirection === 'desc') {
        setSort(null, null);
      }
    } else {
      // New field, start with ascending
      setSort(field, 'asc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Build sorted list of active system users for the Users filter dropdown
  const userFilterOptions = useMemo(() => {
    return activeUsers
      .map((user) => ({
        id: user.id,
        displayName: `${user.firstName} ${user.lastName}`,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [activeUsers]);

  // With server-side filtering/sorting/pagination, cases from the hook are already
  // filtered, sorted, and paginated. Apply only the local "action-due-today" filter.
  const filteredCases = useMemo(() => {
    if (filterParam === 'action-due-today') {
      return cases.filter((caseItem) => {
        if (caseItem.nextAction === 'No further actions scheduled') return false;
        const actionDate = new Date(caseItem.nextActionDate);
        if (actionDate.getFullYear() === 9999) return false;
        const daysDiff = differenceInDays(actionDate, new Date());
        return daysDiff === 0;
      });
    }
    return cases;
  }, [cases, filterParam]);

  // Server-side pagination values
  const totalPages = pagination.totalPages;
  const paginatedCases = filteredCases;

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  // KPI values from the server-side totals query (across all pages)
  const kpis = useMemo(
    () => ({
      myCases: totals?.totalCount ?? 0,
      promisedCases: totals?.promisedCount ?? 0,
      highPriority: totals?.highPriorityCount ?? 0,
      dueToday: totals?.dueTodayCount ?? 0,
      totalOutstanding: totals?.totalOutstanding ?? 0,
      totalBaseOutstanding: totals?.totalBaseOutstanding ?? 0,
      totalDisplayOutstanding: totals?.totalDisplayOutstanding ?? 0,
      totalGross: totals?.totalGross ?? 0,
      totalBaseGross: totals?.totalBaseGross ?? 0,
      totalDisplayGross: totals?.totalDisplayGross ?? 0,
    }),
    [totals],
  );

  const showCaseCurrencyKpi = currencyMode === 'customer' && advancedFilters.currencies.length === 1;

  const handleViewCaseDetails = (caseItem: CollectionCase) => {
    // Navigate to full-screen case details page
    navigate(`/collections/queue/case/${caseItem.id}`, {
      state: { from: 'queue' },
    });
  };

  const handleAction = (action: string, caseItem: CollectionCase) => {
    setSelectedCase(caseItem);
    switch (action) {
      case 'call':
        setLogCallOpen(true);
        break;
      case 'email':
        setSendEmailOpen(true);
        break;
      case 'promise':
        setCreatePromiseOpen(true);
        break;
      default:
        break;
    }
  };
  // handleAction is used in the table row actions
  void handleAction;

  // Checkbox selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredCases.map((c) => c.id));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCaseIds);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCaseIds(newSelected);
  };

  // Bulk action handlers
  const handleBulkAction = (action: string) => {
    if (selectedCaseIds.size === 0) return;

    // For single selection, use the first selected case
    const firstSelectedId = Array.from(selectedCaseIds)[0];
    const firstCase = cases.find((c) => c.id === firstSelectedId);

    if (firstCase) {
      setSelectedCase(firstCase);
      switch (action) {
        case 'call':
          setLogCallOpen(true);
          break;
        case 'email':
          setSendEmailOpen(true);
          break;
        case 'promise':
          setCreatePromiseOpen(true);
          break;
        case 'reassign':
          setReassignUserOpen(true);
          break;
        case 'updateStatus':
          setUpdateStatusOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const selectedCases = useMemo(() => {
    return cases.filter((c) => selectedCaseIds.has(c.id));
  }, [cases, selectedCaseIds]);

  const actionBarTotal = useMemo(() => {
    if (selectedCases.length === 0) {
      return { total: 0, currency: baseCurrency };
    }

    const currencies = new Set(selectedCases.map((c) => c.currency).filter(Boolean));
    const isAllSameCurrency = currencies.size === 1;

    if (isAllSameCurrency) {
      const currency = selectedCases[0].currency || baseCurrency;
      const total = selectedCases.reduce((sum, c) => sum + (c.displayOutstandingAmount ?? 0), 0);
      return { total, currency };
    }

    const total = selectedCases.reduce((sum, c) => sum + (c.baseAmount ?? 0), 0);
    return { total, currency: baseCurrency };
  }, [selectedCases, baseCurrency]);

  // Handler for reassigning user
  const handleReassignUserSave = (userId: string, notes: string) => {
    const caseIds = Array.from(selectedCaseIds).map((id) => Number(id));
    if (caseIds.length === 0) return;

    bulkReassignMutation.mutate(
      { caseIds, assignedToUserId: Number(userId), notes: notes || undefined },
      {
        onSuccess: (data) => {
          toast.success(`Reassigned ${data.updatedCount} case(s)`);
          setSelectedCaseIds(new Set());
        },
        onError: () => {
          toast.error('Failed to reassign cases');
        },
      },
    );
  };

  // Handler for updating status
  const handleUpdateStatusSave = (status: string, notes: string) => {
    const caseIds = Array.from(selectedCaseIds).map((id) => Number(id));
    if (caseIds.length === 0) return;

    bulkUpdateStatusMutation.mutate(
      { caseIds, status, notes: notes || undefined },
      {
        onSuccess: (data) => {
          toast.success(`Updated status for ${data.updatedCount} case(s) to ${status}`);
          setSelectedCaseIds(new Set());
        },
        onError: () => {
          toast.error('Failed to update case status');
        },
      },
    );
  };

  const isAllSelected = filteredCases.length > 0 && selectedCaseIds.size === filteredCases.length;
  const isSomeSelected = selectedCaseIds.size > 0 && selectedCaseIds.size < filteredCases.length;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'destructive';
      case 'Medium':
        return 'default';
      case 'Low':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'On Hold':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'Promised':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'Escalated':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return '';
    }
  };

  const isOverdue = (date: Date) => {
    const d = new Date(date);
    if (d.getFullYear() === 9999) return false;
    return differenceInDays(new Date(), d) > 0;
  };

  const isDueToday = (date: Date) => {
    const d = new Date(date);
    if (d.getFullYear() === 9999) return false;
    return differenceInDays(d, new Date()) === 0;
  };

  // Server-side export: map frontend column IDs to API export field names
  const exportColumnNameMap: Record<string, string> = useMemo(
    () => ({
      caseId: 'CaseNumber',
      priority: 'Priority',
      customerName: 'CustomerName',
      customerAccountNumber: 'CustomerAccountNumber',
      customerEmail: 'CustomerEmail',
      customerPhone: 'CustomerPhone',
      currency: 'CurrencyCode',
      gross: 'DisplayGrossAmount',
      outstanding: 'DisplayOutstandingAmount',
      daysInCollection: 'DaysInCollection',
      transactionCount: 'TransactionCount',
      nextAction: 'NextAction',
      nextActionDate: 'NextActionDate',
      status: 'Status',
      strategy: 'StrategyName',
      stage: 'CurrentStage',
      assignedTo: 'AssignedToName',
      createdAt: 'CreatedAt',
      updatedAt: 'UpdatedAt',
      lastAction: 'LastActionDescription',
    }),
    [],
  );

  // Build columns for server-side export based on visible columns
  const getExportColumns = useCallback((): ColumnInput[] => {
    return baseVisibleColumnDefs
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [baseVisibleColumnDefs, exportColumnNameMap]);

  // Build columns for server-side export with all columns
  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_QUEUE_COLUMNS.filter((col) => col.id in exportColumnNameMap).map((col) => ({
      name: exportColumnNameMap[col.id],
      displayName: col.label,
    }));
  }, [exportColumnNameMap]);

  // Reuse the same filter builder used by the GraphQL queries
  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGraphQLFilterVariable(queryParams),
    [queryParams],
  );

  // Server-side export hook for current view columns
  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'CollectionCases',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `my-cases-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hook for all columns
  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'CollectionCases',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `my-cases-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Handle export to Excel (server-side)
  const handleExportToExcel = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle export all columns to Excel (server-side)
  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Column header label lookup (like Customers page getColumnHeader)
  const getColumnHeader = (colId: string): { label: string; className?: string } => {
    const headers: Record<string, { label: string; className?: string }> = {
      priority: { label: 'Priority' },
      caseId: { label: 'Case Number' },
      customerName: { label: 'Customer' },
      currency: { label: 'Currency' },
      gross: { label: currencyMode === 'base' ? `Gross (${baseCurrency})` : 'Gross', className: 'text-right' },
      outstanding: {
        label: currencyMode === 'base' ? `Outstanding (${baseCurrency})` : 'Outstanding',
        className: 'text-right',
      },
      daysInCollection: { label: 'Days' },
      transactionCount: { label: 'No. of Transactions', className: 'text-right' },
      lastAction: { label: 'Last Action' },
      nextAction: { label: 'Next Action' },
      nextActionDate: { label: 'Action Due Date' },
      status: { label: 'Status' },
      strategy: { label: 'Collection Strategy' },
      stage: { label: 'Stage' },
      customerAccountNumber: { label: 'Account Number' },
      customerEmail: { label: 'Email' },
      customerPhone: { label: 'Phone' },
      assignedTo: { label: 'Assigned To' },
      createdAt: { label: 'Created Date' },
      updatedAt: { label: 'Last Updated' },
    };
    return headers[colId] ?? { label: colId };
  };

  const sortableColumns: string[] = [
    'priority',
    'caseId',
    'customerName',
    'currency',
    'gross',
    'outstanding',
    'daysInCollection',
    'transactionCount',
    'nextAction',
    'nextActionDate',
    'status',
    'strategy',
    'stage',
    'customerAccountNumber',
    'customerEmail',
    'customerPhone',
    'assignedTo',
    'createdAt',
    'updatedAt',
  ];

  const renderColumnCell = (colId: string, caseItem: CollectionCase): React.ReactNode => {
    switch (colId) {
      case 'select':
        return (
          <TableCell key={colId}>
            <Checkbox
              checked={selectedCaseIds.has(caseItem.id)}
              onCheckedChange={(checked) => handleSelectCase(caseItem.id, checked as boolean)}
              aria-label={`Select case ${caseItem.caseId}`}
            />
          </TableCell>
        );
      case 'priority':
        return (
          <TableCell key={colId}>
            <Badge variant={getPriorityBadgeVariant(caseItem.priority)}>{caseItem.priority}</Badge>
          </TableCell>
        );
      case 'caseId':
        return (
          <TableCell key={colId} className="font-medium">
            <Button
              variant="link"
              className="h-auto p-0 font-medium hover:underline"
              onClick={() => handleViewCaseDetails(caseItem)}
            >
              {caseItem.caseNumber || caseItem.caseId}
            </Button>
          </TableCell>
        );
      case 'customerName':
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate max-w-[200px] block">{caseItem.customerName}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{caseItem.customerName}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      case 'currency':
        return <TableCell key={colId}>{caseItem.currency || '\u2014'}</TableCell>;
      case 'gross': {
        const displayGross = currencyMode === 'base' ? (caseItem.grossAmount ?? 0) : (caseItem.displayGrossAmount ?? 0);
        const displayCurrency = currencyMode === 'base' ? baseCurrency : caseItem.currency || baseCurrency;
        return (
          <TableCell key={colId} className="text-right font-medium">
            {getCurrencySymbol(displayCurrency)}
            {displayGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
        );
      }
      case 'outstanding': {
        const displayOutstanding =
          currencyMode === 'base' ? (caseItem.baseAmount ?? 0) : (caseItem.displayOutstandingAmount ?? 0);
        const displayCurrency = currencyMode === 'base' ? baseCurrency : caseItem.currency || baseCurrency;
        return (
          <TableCell key={colId} className="text-right font-medium">
            {getCurrencySymbol(displayCurrency)}
            {displayOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TableCell>
        );
      }
      case 'daysInCollection':
        return (
          <TableCell key={colId}>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm">{caseItem.daysInCollection}d</span>
            </div>
          </TableCell>
        );
      case 'transactionCount':
        return (
          <TableCell key={colId} className="text-right">
            <span className="text-sm">{caseItem.transactionCount ?? 0}</span>
          </TableCell>
        );
      case 'lastAction': {
        const latestAction = getLatestAction(caseItem);
        return (
          <TableCell key={colId}>
            <LastActionCell lastAction={latestAction} />
          </TableCell>
        );
      }
      case 'nextAction':
        return (
          <TableCell key={colId}>
            <p className="text-sm">{caseItem.nextAction}</p>
          </TableCell>
        );
      case 'nextActionDate':
        return (
          <TableCell key={colId}>
            <p
              className={cn(
                'text-sm',
                caseItem.nextAction === 'No further actions scheduled'
                  ? 'text-muted-foreground'
                  : isOverdue(caseItem.nextActionDate)
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : isDueToday(caseItem.nextActionDate)
                      ? 'text-amber-600 dark:text-amber-400 font-medium'
                      : 'text-muted-foreground',
              )}
            >
              {caseItem.nextAction === 'No further actions scheduled'
                ? 'n/a'
                : formatDateDDMMYYYY(caseItem.nextActionDate.toISOString())}
            </p>
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={colId}>
            <div className="flex flex-col gap-1">
              <Badge className={getStatusBadgeColor(caseItem.status)}>{caseItem.status}</Badge>
              {caseItem.status === 'Promised' && caseItem.currentPromise && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs text-muted-foreground">
                        Due: {formatDate(caseItem.currentPromise.promiseDate.toISOString())}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="space-y-1">
                        <p className="font-medium">Promise Details</p>
                        <p className="text-xs">
                          Amount: {currencySymbol}
                          {caseItem.currentPromise.promisedAmount.toLocaleString()}
                        </p>
                        <p className="text-xs">Due: {formatDate(caseItem.currentPromise.promiseDate.toISOString())}</p>
                        <p className="text-xs">Method: {caseItem.currentPromise.confirmationMethod}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </TableCell>
        );
      case 'strategy':
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm truncate max-w-[200px] block">{caseItem.strategy.name}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{caseItem.strategy.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      case 'stage':
        return (
          <TableCell key={colId}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{
                          width: `${(caseItem.strategyPhase.currentPhase / caseItem.strategyPhase.totalPhases) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {caseItem.strategyPhase.currentPhase}/{caseItem.strategyPhase.totalPhases}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{caseItem.strategyPhase.phaseName}</p>
                  <p className="text-xs text-muted-foreground">
                    Phase {caseItem.strategyPhase.currentPhase} of {caseItem.strategyPhase.totalPhases}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
        );
      case 'customerAccountNumber':
        return <TableCell key={colId}>{caseItem.customerAccountNumber}</TableCell>;
      case 'customerEmail':
        return <TableCell key={colId}>{caseItem.customerEmail}</TableCell>;
      case 'customerPhone':
        return <TableCell key={colId}>{caseItem.customerPhone}</TableCell>;
      case 'assignedTo':
        return <TableCell key={colId}>{caseItem.assignedTo ?? '\u2014'}</TableCell>;
      case 'createdAt':
        return <TableCell key={colId}>{formatDateTime(caseItem.createdAt.toISOString())}</TableCell>;
      case 'updatedAt':
        return <TableCell key={colId}>{formatDateTime(caseItem.updatedAt.toISOString())}</TableCell>;
      default:
        return null;
    }
  };

  // Safety check - if cases failed to load, show error
  if (!cases || cases.length === 0) {
    console.warn('[CollectionsQueue] No cases available');
  }

  console.log('[CollectionsQueue] Rendering with', cases?.length || 0, 'cases');

  return (
    <div className="flex flex-col gap-6 p-6 h-full min-h-[600px]">
      {/* Header */}
      <div className="shrink-0 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Collections Queue</h1>
          <p className="text-muted-foreground">Manage your assigned collection cases</p>
        </div>
        <div className="flex items-center gap-2">
          <FeatureFeedbackCollector feature="Collections Queue" />
          {canWrite && (
            <Button
              onClick={() => navigate('/collections/queue/create', { state: { from: 'queue' } })}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Create New Case
            </Button>
          )}
          <HelpSupportButton screenId="collections-queue" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="shrink-0 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <KPICard
          title="My Cases"
          value={kpis.myCases}
          subtitle="Total cases matching filters"
          icon={Inbox}
          colorScheme="blue"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="Promised"
          value={kpis.promisedCases}
          subtitle="Payment commitments"
          icon={HandshakeIcon}
          colorScheme="blue"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="High Priority"
          value={kpis.highPriority}
          subtitle="Requiring immediate attention"
          icon={AlertTriangle}
          colorScheme="red"
          isLoading={isTotalsLoading}
        />
        <KPICard
          title="Due Today"
          value={kpis.dueToday}
          subtitle="Actions scheduled for today"
          icon={Calendar}
          colorScheme="amber"
          isLoading={isTotalsLoading}
        />
        <Card className="transition-all border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50/30 to-transparent dark:from-purple-950/20">
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-purple-100 flex-shrink-0">
                  <CurrencyIcon currencyCode={selectedCompany?.currency} className="h-4 w-4 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium">Total Outstanding</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Total Outstanding info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {showCaseCurrencyKpi
                        ? `Total for ${advancedFilters.currencies[0]} cases only. Values shown in ${advancedFilters.currencies[0]}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all collection cases.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {isTotalsLoading ? (
                <div className="mt-1 h-8 w-24 animate-pulse rounded bg-muted" />
              ) : (
                <div className="text-2xl font-bold mt-1">
                  {formatCurrency(
                    showCaseCurrencyKpi ? kpis.totalDisplayOutstanding : kpis.totalBaseOutstanding,
                    showCaseCurrencyKpi ? advancedFilters.currencies[0] : baseCurrency,
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {showCaseCurrencyKpi
                  ? `Total for ${advancedFilters.currencies[0]} cases`
                  : 'Sum of outstanding amounts'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cases Table — sticky toolbar + table wrapper (matches Customers page) */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
          <div className="flex items-center gap-4">
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
              {isFiltered ? '' : 'My '}Cases ({kpis.myCases})
            </span>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by case ID, customer name or account number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <CurrencyToggle
              value={currencyMode}
              onChange={setCurrencyMode}
              nonBaseLabel="Case Currency"
              baseLabel={`Base Currency (${baseCurrency})`}
            />
            <Button variant="outline" onClick={handleOpenMoreFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportToExcel}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setColumnConfigOpen(true)} className="h-8 w-8">
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {!isFiltered && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200 mt-2">
              <Filter className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700">
                Default filter active: <span className="font-medium">Excluding Closed</span>
              </span>
              <button
                onClick={() => {
                  setAdvancedFilters({ ...advancedFilters, statuses: [] });
                  toast.info('Default filter removed');
                }}
                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Remove default filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Table card */}
        <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {/* Scrollable table area */}
            <div className="flex-1 min-h-[200px] overflow-auto">
              <table className="w-full caption-bottom text-sm [&_tr]:divide-x [&_tr]:divide-gray-100">
                <thead className="sticky top-0 z-10 bg-white shadow-sm">
                  <tr>
                    <th className="h-10 text-center font-bold text-gray-700 w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-200">
                      <div className="flex items-center justify-center h-full">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all cases"
                          className={cn(isSomeSelected && !isAllSelected && 'data-[state=checked]:bg-primary')}
                        />
                      </div>
                    </th>
                    {visibleColumns
                      .filter((colId) => colId !== 'select')
                      .map((columnId) => {
                        const header = getColumnHeader(columnId);
                        const isSortable = sortableColumns.includes(columnId);
                        const colWidth = localColumnWidths[columnId];
                        const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
                        const isPinLeft = pinnedLeft.includes(columnId);
                        const isPinRight = pinnedRight.includes(columnId);
                        return (
                          <th
                            key={columnId}
                            ref={measureRef(columnId)}
                            draggable
                            onDragStart={() => setDraggedColumn(columnId)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragOverColumn(columnId);
                            }}
                            onDragEnd={() => {
                              setDraggedColumn(null);
                              setDragOverColumn(null);
                            }}
                            onDrop={() => {
                              if (!draggedColumn || draggedColumn === columnId) return;
                              const newCols = [...visibleColumns];
                              const fromIdx = newCols.indexOf(draggedColumn);
                              const toIdx = newCols.indexOf(columnId);
                              newCols.splice(fromIdx, 1);
                              newCols.splice(toIdx, 0, draggedColumn);
                              setVisibleColumns(newCols);
                              setDraggedColumn(null);
                              setDragOverColumn(null);
                            }}
                            style={getPinnedColumnStyle(columnId, pinnedLeft, pinnedRight, localColumnWidths, {
                              zIndex: 20,
                              unpinnedPosition: 'relative',
                              measuredWidths: measuredColumnWidths,
                              columnOrder: visibleColumns,
                            })}
                            className={`h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none bg-white
                            ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-400' : ''}
                            ${draggedColumn === columnId ? 'opacity-50' : ''}
                            ${isPinLeft || isPinRight ? 'shadow-md' : ''}
                            ${header.className || ''}`}
                          >
                            <div className="flex items-center justify-between gap-1 w-full">
                              {isSortable ? (
                                <button
                                  onClick={() => handleSort(columnId as SortField)}
                                  className="flex items-center gap-1 hover:text-gray-900 transition-colors font-bold"
                                >
                                  <span>{header.label}</span>
                                  <span className="text-gray-500">{getSortIcon(columnId as SortField)}</span>
                                </button>
                              ) : (
                                <span className="font-bold">{header.label}</span>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <button className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                                    <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="z-[9999] w-48">
                                  <DropdownMenuItem onClick={() => handlePinLeft(columnId)}>
                                    <Pin className="h-4 w-4 mr-2" />
                                    {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePinRight(columnId)}>
                                    <Pin className="h-4 w-4 mr-2 rotate-180" />
                                    {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={handleResetColumnWidths}>
                                    <Maximize2 className="h-4 w-4 mr-2" />
                                    Reset Column Widths
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={handleResetColumnOrder}>
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Reset Column Order
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            {/* Resize handle */}
                            <button
                              type="button"
                              aria-label="Resize column"
                              className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10 p-0 border-0 bg-transparent"
                              onMouseDown={(e) =>
                                handleResizeMouseDown(
                                  e,
                                  columnId,
                                  colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </th>
                        );
                      })}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Loading cases...</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!isLoading && paginatedCases.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <p className="text-sm text-muted-foreground">No cases found</p>
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    paginatedCases.length > 0 &&
                    paginatedCases.map((caseItem, index) => (
                      <TableRow
                        key={caseItem.id}
                        className={cn(
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          selectedCaseIds.has(caseItem.id) && 'bg-muted/50',
                        )}
                      >
                        {visibleColumns.map((colId) => {
                          const cell = renderColumnCell(colId, caseItem);
                          const isPinLeft = pinnedLeft.includes(colId);
                          const isPinRight = pinnedRight.includes(colId);
                          if (!React.isValidElement(cell) || (!isPinLeft && !isPinRight)) {
                            return <React.Fragment key={colId}>{cell}</React.Fragment>;
                          }
                          const pinnedCell = cell as React.ReactElement<React.ComponentProps<typeof TableCell>>;
                          return React.cloneElement(pinnedCell, {
                            key: colId,
                            style: {
                              ...pinnedCell.props.style,
                              ...getPinnedColumnStyle(colId, pinnedLeft, pinnedRight, localColumnWidths, {
                                measuredWidths: measuredColumnWidths,
                                columnOrder: visibleColumns,
                              }),
                            },
                            className: `${pinnedCell.props.className ?? ''} shadow-md ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`,
                          });
                        })}
                      </TableRow>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer — outside scroll area, always visible (matches Customers) */}
            <div className="shrink-0 border-t px-4 py-3 flex items-center justify-between pr-16">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((page) =>
                    typeof page === 'string' ? (
                      <span key={page} className="px-1 text-muted-foreground">
                        …
                      </span>
                    ) : (
                      <Button
                        key={`page-${page}`}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Drawer (Sheet) — matches Customers page pattern */}
      <Sheet open={moreFiltersOpen} onOpenChange={handleCloseMoreFilters}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col overflow-hidden">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>Filter your collection cases</SheetDescription>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-4 pl-4 mt-6">
            <div className="space-y-4 pb-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <div className="space-y-3">
                  {(['Active', 'Promised', 'Escalated', 'On Hold', 'Closed'] as const).map((status) => (
                    <label key={status} className="flex items-center gap-3 cursor-pointer">
                      <Checkbox
                        checked={draftFilters.statuses.includes(status)}
                        onCheckedChange={(checked) => {
                          setDraftFilters((prev) => ({
                            ...prev,
                            statuses: checked ? [...prev.statuses, status] : prev.statuses.filter((s) => s !== status),
                          }));
                        }}
                      />
                      <span className="text-sm">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Priority</Label>
                <Select
                  value={draftFilters.priority}
                  onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Assigned User</Label>
                <Select
                  value={draftFilters.assignedUser}
                  onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, assignedUser: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {userFilterOptions.map((user) => (
                      <SelectItem key={user.id} value={String(user.id)}>
                        {user.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Currency</Label>
                <MultiSelect
                  options={currencyFilterOptions.map((currency) => ({
                    value: currency,
                    label: currency,
                  }))}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={draftFilters.currencies}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      currencies: values,
                    })
                  }
                />
              </div>

              <Separator />

              {/* P1: Action Due Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Action Due Date Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={draftFilters.nextActionDateFrom}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, nextActionDateFrom: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={draftFilters.nextActionDateTo}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, nextActionDateTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* P1: Outstanding Amount Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Outstanding Amount Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      step="any"
                      aria-invalid={draftRangeErrors.outstanding}
                      value={draftFilters.outstandingMin ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          outstandingMin: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      step="any"
                      aria-invalid={draftRangeErrors.outstanding}
                      value={draftFilters.outstandingMax ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          outstandingMax: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
                {draftRangeErrors.outstanding && (
                  <p role="alert" className="text-sm text-destructive">
                    {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                  </p>
                )}
              </div>

              {/* P1: Days in Collection Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Days in Collection Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      aria-invalid={draftRangeErrors.daysInCollection}
                      value={draftFilters.daysInCollectionMin ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          daysInCollectionMin: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      aria-invalid={draftRangeErrors.daysInCollection}
                      value={draftFilters.daysInCollectionMax ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          daysInCollectionMax: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
                {draftRangeErrors.daysInCollection && (
                  <p role="alert" className="text-sm text-destructive">
                    {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                  </p>
                )}
              </div>

              <Separator />

              {/* P2: Collection Strategy */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Collection Strategy</Label>
                <MultiSelect
                  options={strategyFilterOptions}
                  placeholder="Select strategies..."
                  emptyMessage="No strategies found."
                  value={draftFilters.strategies}
                  onValueChange={(values) => setDraftFilters((prev) => ({ ...prev, strategies: values }))}
                  maxDisplay={2}
                />
              </div>

              {/* P2: Collection Stage Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Collection Stage Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      aria-invalid={draftRangeErrors.stage}
                      value={draftFilters.stageMin ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          stageMin: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      aria-invalid={draftRangeErrors.stage}
                      value={draftFilters.stageMax ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          stageMax: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
                {draftRangeErrors.stage && (
                  <p role="alert" className="text-sm text-destructive">
                    {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                  </p>
                )}
              </div>

              <Separator />

              {/* P3: Gross Amount Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Gross Amount Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Min</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      step="any"
                      aria-invalid={draftRangeErrors.gross}
                      value={draftFilters.grossMin ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          grossMin: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Max</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      step="any"
                      aria-invalid={draftRangeErrors.gross}
                      value={draftFilters.grossMax ?? ''}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          grossMax: parseRangeInput(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
                {draftRangeErrors.gross && (
                  <p role="alert" className="text-sm text-destructive">
                    {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                  </p>
                )}
              </div>

              {/* P3: Created Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Created Date Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={draftFilters.createdFrom}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, createdFrom: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={draftFilters.createdTo}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, createdTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* P4: Last Updated Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Last Updated Date Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={draftFilters.updatedFrom}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, updatedFrom: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={draftFilters.updatedTo}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, updatedTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* P4: Last Action Type */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Last Action Type</Label>
                <MultiSelect
                  options={actionTypeOptions}
                  placeholder="Select action types..."
                  emptyMessage="No action types found."
                  value={draftFilters.lastActionTypes}
                  onValueChange={(values) => setDraftFilters((prev) => ({ ...prev, lastActionTypes: values }))}
                  maxDisplay={2}
                />
              </div>
            </div>
          </div>

          <SheetFooter className="flex-none p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button variant="outline" className="flex-1" onClick={handleClearDraftFilters}>
                Clear All
              </Button>
              <Button className="flex-1" disabled={hasInvalidRange} onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Floating Quick Actions Menu */}
      {selectedCaseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
                    >
                      {selectedCaseIds.size}
                    </Badge>
                    <span className="font-medium">{selectedCaseIds.size === 1 ? 'case' : 'cases'} selected</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span>
                      Total:{' '}
                      <span className="font-semibold">
                        {formatCurrency(actionBarTotal.total, actionBarTotal.currency)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {selectedCaseIds.size === 1 && (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => handleBulkAction('call')} className="gap-2">
                      <Phone className="h-4 w-4" />
                      Log Call
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => handleBulkAction('email')} className="gap-2">
                      <Mail className="h-4 w-4" />
                      Send Email
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => handleBulkAction('promise')} className="gap-2">
                      <FileText className="h-4 w-4" />
                      Record Promise
                    </Button>
                  </>
                )}
                <Button variant="secondary" size="sm" onClick={() => handleBulkAction('reassign')} className="gap-2">
                  <UserCog className="h-4 w-4" />
                  Reassign User
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction('updateStatus')}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Update Status
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCaseIds(new Set())}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {selectedCase && (
        <>
          <LogCallModal
            open={logCallOpen}
            onOpenChange={setLogCallOpen}
            customerName={selectedCase.customerName}
            customerPhone={selectedCase.customerPhone}
            caseId={selectedCase.id}
            customerId={selectedCase.customerId}
            defaultContactId={selectedCase.contactId}
            onSuccess={() => {
              if (!selectedCase.strategy.id) {
                setShowUpdateNextAction(true);
              }
            }}
          />
          <CaseSendEmailModal
            open={sendEmailOpen}
            onOpenChange={setSendEmailOpen}
            caseId={selectedCase.id}
            customerId={selectedCase.customerId}
            customerName={selectedCase.customerName}
            accountNumber={selectedCase.customerAccountNumber || ''}
            onStageConfirmationRequired={(confirmation) => setStageConfirmation(confirmation)}
            onSuccess={() => {
              if (!selectedCase.strategy.id) {
                setShowUpdateNextAction(true);
              }
            }}
          />

          {/* Forced confirmation after a manual email on an active-strategy case */}
          {stageConfirmation && (
            <StageSkipConfirmationModal
              open
              nextStageName={stageConfirmation.nextStageName}
              isSubmitting={recordStageSkipDecision.isPending}
              onConfirm={(markComplete) => {
                recordStageSkipDecision.mutate(
                  {
                    caseId: selectedCase.id,
                    nextStageSortOrder: stageConfirmation.nextStageSortOrder,
                    markComplete,
                  },
                  {
                    onSuccess: () => {
                      toast.success(
                        markComplete
                          ? `${stageConfirmation.nextStageName} marked complete and skipped`
                          : `${stageConfirmation.nextStageName} kept; automation continues as scheduled`,
                      );
                      setStageConfirmation(null);
                      queryClient.invalidateQueries({ queryKey: ['collection-cases-list'] });
                      queryClient.invalidateQueries({ queryKey: ['collection-cases-totals'] });
                    },
                    onError: () => {
                      toast.error('Failed to record your decision. Please try again.');
                    },
                  },
                );
              }}
            />
          )}

          <UpdateNextActionDialog
            open={showUpdateNextAction}
            onOpenChange={setShowUpdateNextAction}
            caseData={selectedCase}
            onUpdated={() => {}}
          />
        </>
      )}

      {/* Create Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseOpen}
        onOpenChange={setCreatePromiseOpen}
        prePopulatedCustomer={
          selectedCase
            ? {
                customerId: selectedCase.customerId,
                customerName: selectedCase.customerName,
                accountNumber: selectedCase.customerAccountNumber || '',
              }
            : undefined
        }
        prePopulatedTransactions={
          selectedCase
            ? (() => {
                const txns =
                  caseLinkedTransactionIds.length > 0
                    ? caseTransactionsData.filter((t) => caseLinkedTransactionIds.includes(t.id))
                    : caseTransactionsData;
                return txns.map((transaction) => {
                  const daysOverdue = transaction.dueDate
                    ? Math.max(
                        0,
                        Math.floor(
                          (new Date().getTime() - new Date(transaction.dueDate).getTime()) / (1000 * 60 * 60 * 24),
                        ),
                      )
                    : 0;
                  return {
                    id: transaction.id.toString(),
                    headerReference: transaction.referenceNumber,
                    invoiceDate: new Date(transaction.transactionDate),
                    originalAmount: transaction.amount,
                    balanceRemaining: transaction.outstandingAmount || transaction.amount,
                    daysOverdue,
                    status: transaction.status ?? null,
                  };
                });
              })()
            : undefined
        }
        prePopulatedCaseId={selectedCase?.id}
        prePopulatedCaseNumber={selectedCase?.caseNumber || selectedCase?.caseId}
        triggerSource="action-bar"
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Reassign User Modal */}
      <ReassignUserModal
        open={reassignUserOpen}
        onOpenChange={setReassignUserOpen}
        selectedCount={selectedCaseIds.size}
        onSave={handleReassignUserSave}
        users={reassignableUsers}
        entityType="case"
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={updateStatusOpen}
        onOpenChange={setUpdateStatusOpen}
        selectedCount={selectedCaseIds.size}
        onSave={handleUpdateStatusSave}
        entityType="case"
      />
    </div>
  );
}
